import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ParentHeader from "../ParentHeader/ParentHeader";
import Loader from "../../Components/Loader/Loader";
import favIcon from "../../Assets/favicon.ico";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { useMaterialSymbols } from "../../Teacher/Oireachtas/components";
import { dayRange, eventDayOf, longDay, placeLine } from "../EventSite/eventSiteModel";
import { competitionSections, filterSections, schoolCompetitions, schoolDancers, yourCompetitions } from "./countsModel";
import { listRows, oireachtasSchoolDancers, oireachtasSections, oireachtasYours, releaseLine, roundTabs } from "./oireachtasCountsModel";
import "../../Site/site.css";
import "../EventSite/eventSite.css";
import "./competitionCounts.css";

// Competitors (MC2 76145074; briefly "Competition counts"): how many dancers are
// in each competition of the feis, open any time - the family's own
// competitions on top, then every competition, one level at a time (the
// levels across the top). Who is in a competition (tap through to it) only
// once the organiser marks merges and splits done; until then the arrow is
// grey (Devin 2026-10-18). A teacher gets their school's dancers on top
// instead, each with what they are entered in, then what everyone sees; a
// competition under one of them opens to the school's dancers in it before
// merges and splits are done, the whole field after.

const API = process.env.REACT_APP_BASE_URL;
const Icon = ({ name }) => (
  <span className="msr" aria-hidden="true">
    {name}
  </span>
);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const Row = ({ item, sub, level, yours, meta, namesShown, onOpen }) => {
  // an Oireachtas competition carries its own `open` (its running order is out)
  const canOpen = (item.open !== undefined ? item.open : namesShown || item.fromSchool > 0) && item.count > 0;
  const waitNote = item.open !== undefined ? "The running order shows here once it's out" : "Names show once the organiser finishes merges and splits";
  return (
    <li className={`cc-item${sub ? " sub" : ""}${item.count ? "" : " empty"}`}>
      <button
        type="button"
        className="cc-row"
        onClick={onOpen}
        disabled={!canOpen}
        title={item.count > 0 && !canOpen ? waitNote : undefined}
      >
        <span className="cc-code">{item.code}</span>
        <span className="cc-name">
          {item.name}
          {item.age && !sub && <span className="cc-age"> {item.age}</span>}
          {(level || yours || meta) && <span className="cc-meta">{[level, yours && yours.join(", "), meta].filter(Boolean).join(" · ")}</span>}
        </span>
        <span className="cc-count">{item.count || "–"}</span>
        <span className={`cc-arrow${item.count ? "" : " none"}${canOpen ? "" : " off"}`}>
          <Icon name="chevron_right" />
        </span>
      </button>
    </li>
  );
};

// One competition's dancers (a click-through from its row).
const Competition = ({ item, level, ours }) => (
  <section className="cc-sec cc-one">
    <div className="cc-one-head">
      <span className="cc-one-level">{[level, item.code].filter(Boolean).join(" · ")}</span>
      <span role="heading" aria-level={2} className="cc-one-name">
        {item.name}
        {item.age && <span className="cc-age"> {item.age}</span>}
      </span>
      <span className="cc-one-count">{item.onlySchool ? `${item.fromSchool} from your school` : plural(item.count, "dancer", "dancers")}</span>
    </div>
    <ol className="cc-list cc-who">
      {item.dancers.map((d) => (
        <li key={d.id} className={ours.has(d.name.toLowerCase()) ? "ours" : ""}>
          <span className="cc-num">{d.number}</span>
          <span className="cc-who-name">{d.name}</span>
          <span className="cc-school">{d.school}</span>
        </li>
      ))}
    </ol>
  </section>
);

// The levels in one scrolling row; when more are off to a side, a fade and
// a green arrow on that side (as the registration's level bar does).
const LevelTabs = ({ sections, activeKey, onPick, label = "Levels" }) => {
  const barRef = useRef(null);
  const [more, setMore] = useState({ left: false, right: false });
  const measure = () => {
    const bar = barRef.current;
    if (!bar) return;
    const left = bar.scrollLeft > 2;
    const right = bar.scrollLeft + bar.clientWidth < bar.scrollWidth - 2;
    setMore((m) => (m.left === left && m.right === right ? m : { left, right }));
  };
  useEffect(() => {
    const bar = barRef.current;
    const el = bar ? [...bar.children].find((c) => c.dataset.key === activeKey) : null;
    if (bar && el && typeof bar.scrollTo === "function") {
      const left = el.offsetLeft - (bar.clientWidth - el.clientWidth) / 2;
      bar.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
    }
    measure();
  }, [activeKey, sections.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const step = (dir) => {
    const i = sections.findIndex((s) => s.key === activeKey);
    const next = sections[i + dir];
    if (next) onPick(next.key);
    else barRef.current?.scrollBy?.({ left: dir * barRef.current.clientWidth * 0.6, behavior: "smooth" });
  };
  return (
    <div className={`cc-tabs${more.left ? " more-left" : ""}${more.right ? " more-right" : ""}`}>
      {more.left && (
        <button type="button" className="cc-tab-arrow left" aria-label="Previous level" onClick={() => step(-1)}>
          <Icon name="chevron_left" />
        </button>
      )}
      {more.right && (
        <button type="button" className="cc-tab-arrow right" aria-label="Next level" onClick={() => step(1)}>
          <Icon name="chevron_right" />
        </button>
      )}
      <div className="cc-tabs-in" ref={barRef} role="group" aria-label={label} onScroll={measure}>
        {sections.map((sec) => (
          <button
            key={sec.key}
            data-key={sec.key}
            type="button"
            aria-pressed={sec.key === activeKey}
            className={sec.key === activeKey ? "on" : ""}
            onClick={() => onPick(sec.key)}
          >
            {sec.title}
          </button>
        ))}
      </div>
    </div>
  );
};

// One Oireachtas competition's running order (card beb095f8): a plain list of
// names before the numbers release, then a tab per round with numbers.
const OireachtasCompetition = ({ item, level, detail, round, onRound, onRetry }) => {
  const tabs = roundTabs(detail);
  const active = round || tabs[0]?.key;
  const rows = listRows(detail, active);
  return (
    <section className="cc-sec cc-one">
      <div className="cc-one-head">
        <span className="cc-one-level">{[level, item.code].filter(Boolean).join(" · ")}</span>
        <span role="heading" aria-level={2} className="cc-one-name">
          {item.name}
        </span>
        <span className="cc-one-count">{plural(item.count, detail?.teams ? "team" : "dancer", detail?.teams ? "teams" : "dancers")}</span>
      </div>
      {!detail && <Loader />}
      {detail?.failed && (
        <p className="cc-hint">
          The running order didn't load.{" "}
          <button type="button" className="cc-retry" onClick={onRetry}>
            Try again
          </button>
        </p>
      )}
      {detail && !detail.failed && tabs.length > 1 && <LevelTabs sections={tabs} activeKey={active} onPick={onRound} label="Rounds" />}
      {detail && !detail.failed && <p className="cc-hint">{releaseLine(detail, active)}</p>}
      {rows.length > 0 && (
        <ol className={`cc-list cc-who${detail?.released ? "" : " cc-names-only"}`}>
          {rows.map((d) => (
            <li key={d.id} className={d.yours ? "ours" : ""}>
              <span className="cc-num">{d.number}</span>
              <span className="cc-who-name">{d.name}</span>
              <span className="cc-school">{d.school}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

const CompetitionCounts = () => {
  useMaterialSymbols();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const eventId = params.get("id");
  const compId = params.get("comp");
  // an Oireachtas (card beb095f8): same page, its own data (oireachtasCountsModel)
  const isOir = params.get("kind") === "oireachtas";
  const [detail, setDetail] = useState(null);
  const [round, setRound] = useState(null);
  // "mine" (Your competitions / Your students) or "all", like the results
  // page's My Results / All Results (Devin 2026-10-22); null = the default
  const [tab, setTab] = useState(null);
  const [retry, setRetry] = useState(0); // a failed running-order load tries again
  const isTeacher = localStorage.getItem("isTeacher") === "true";
  const signedIn = !!localStorage.getItem("accessToken");

  const [event, setEvent] = useState(null);
  const [data, setData] = useState(null);
  const [mine, setMine] = useState([]);
  const [school, setSchool] = useState(null);
  const [level, setLevel] = useState(null);
  const [query, setQuery] = useState("");
  const listScroll = useRef(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        if (isOir) {
          const res = await serviceConsumer("GET", `${API}/oireachtas/${eventId}/competitor-lists`);
          setEvent(res?.event);
          document.title = `Feis.link - ${res?.event?.name} - Competitors`;
          if (res?.school) setSchool(res.school);
          setData(res || {});
          return;
        }
        const res = await serviceConsumer("GET", `${API}/feis/${eventId}`);
        const feis = res?.Feis;
        setEvent(feis);
        document.title = `Feis.link - ${feis?.name} - Competitors`;
        // the counts are always open; a teacher also gets their own school's list
        if (isTeacher) {
          const sr = await serviceConsumer("GET", `${API}/feis/${eventId}/dancers/school`, { syllabus: 1 }).catch(() => null);
          setSchool(sr || null);
        }
        setData((await serviceConsumer("GET", `${API}/feis/${eventId}/dancers`, { syllabus: 1 })) || {});
        // the family's own entries, for "Your competitions"
        if (signedIn) {
          serviceConsumer("GET", `${API}/feis/registered/dancers/${eventId}`)
            .then((r) => setMine(r?.result || []))
            .catch(() => {});
        }
      } catch (e) {
        navigate("/pageNotFound", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // an opened Oireachtas competition's running order
  useEffect(() => {
    if (!isOir || !compId) return undefined;
    let gone = false;
    setDetail(null);
    setRound(null);
    serviceConsumer("GET", `${API}/oireachtas/${eventId}/competitor-lists/${compId}`)
      .then((d) => !gone && setDetail(d && d.success !== false ? d : { failed: true }))
      // a failed load is not "no draw yet" (code review d03b2f7a R1-F1)
      .catch(() => !gone && setDetail({ failed: true }));
    return () => {
      gone = true;
    };
  }, [isOir, eventId, compId, retry]);

  const rows = data?.result || [];
  const sections = useMemo(
    () => (isOir ? oireachtasSections(data?.competitions) : competitionSections(data?.syllabus, data?.entryGroups, rows)),
    [data], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const shown = useMemo(() => filterSections(sections, query, { empty: true }), [sections, query]);
  const yours = useMemo(() => (isOir ? oireachtasYours(sections, data?.mine) : yourCompetitions(sections, mine)), [sections, mine, data]); // eslint-disable-line react-hooks/exhaustive-deps
  const schoolSections = useMemo(() => (school && !isOir ? competitionSections(school.syllabus, school.entryGroups, school.result || []) : []), [school]); // eslint-disable-line react-hooks/exhaustive-deps
  const ourSchool = useMemo(() => schoolCompetitions(schoolSections, sections), [schoolSections, sections]);
  const kids = useMemo(
    () => (isOir ? oireachtasSchoolDancers(sections, school) : schoolDancers(school?.result, ourSchool)),
    [school, ourSchool, sections], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // the view tabs: Your competitions (a teacher: Your students) or All
  const mineCount = isTeacher ? kids.length : yours.length;
  const hasMine = mineCount > 0;
  const view = hasMine ? tab || "mine" : "all";
  // several of the family's dancers: each row says whose it is
  const manyKids = isOir ? new Set(yours.flatMap((i) => i.yours || [])).size > 1 : mine.length > 1;
  const withEntries = sections.reduce(
    (n, s) => n + s.items.filter((i) => i.count).length + s.items.reduce((m, i) => m + i.subs.filter((x) => x.count).length, 0),
    0,
  );
  const searching = query.trim().length > 0;
  // one level at a time; a search looks across all of them
  const current = shown.find((s) => s.key === level) || shown[0];
  const visible = searching ? shown : current ? [current] : [];

  const place = placeLine(event);
  const when = isOir ? dayRange(event?.eventDays) : longDay(eventDayOf(event));
  // names come with the list only once merges and splits are done
  const namesShown = !!data && !data.namesHidden && rows.some((r) => r?.dancerData?.fname);
  // a competition opens as its own view (?comp=), so Back returns to the list
  const openComp = (id) => {
    listScroll.current = window.scrollY;
    navigate(`?id=${eventId}${isOir ? "&kind=oireachtas" : ""}&comp=${id}`);
    window.scrollTo(0, 0);
  };
  // (only once names are out; an early link just shows the list)
  const opened = useMemo(() => {
    if (!compId) return null;
    if (isOir) {
      for (const sec of sections) {
        const i = sec.items.find((x) => x.id === compId);
        if (i) return { item: i, level: sec.title };
      }
      return null;
    }
    // a teacher's own school's dancers open before names are out
    const own = ourSchool.find((x) => x.id === compId);
    if (own && !namesShown) return { item: { ...own, onlySchool: true }, level: own.level };
    if (!namesShown) return null;
    for (const sec of sections) {
      for (const i of sec.items) {
        if (i.id === compId) return { item: i, level: sec.title };
        const sub = i.subs.find((x) => x.id === compId);
        if (sub) return { item: { ...sub, name: `${i.name} · 2nd chance`, age: i.age }, level: sec.title };
      }
    }
    return null;
  }, [compId, sections, namesShown, ourSchool]);
  // highlighted in a competition's list: the family's dancers, or a teacher's school's
  const ours = useMemo(
    () =>
      new Set(
        [...mine.map((r) => r?.dancerData), ...(school?.result || []).map((r) => r?.dancerData)]
          .map((d) => [d?.fname, d?.lname].filter(Boolean).join(" ").toLowerCase())
          .filter(Boolean),
      ),
    [mine, school],
  );
  useEffect(() => {
    if (!compId && listScroll.current) window.scrollTo(0, listScroll.current);
  }, [compId]);

  return (
    <div className="site es cc">
      <ParentHeader />
      {loading && <Loader />}
      <section className="hero es-hero">
        <div className="wrap">
          <button type="button" className="es-back" onClick={() => window.history.go(-1)}>
            <Icon name="arrow_back" /> Back
          </button>
          <div className="es-id">
            <img className="crest xl" src={event?.logo || favIcon} alt="" />
            <div>
              <span className="eyebrow">Competitors</span>
              <h1 className="es-name">{event?.name}</h1>
              <span className="meta es-when">{[when, place].filter(Boolean).join(" · ")}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="wrap es-body cc-body">
        {data && (
          <>
            {opened && isOir && (
              <OireachtasCompetition
                item={opened.item}
                level={opened.level}
                detail={detail}
                round={round}
                onRound={setRound}
                onRetry={() => setRetry((n) => n + 1)}
              />
            )}
            {opened && !isOir && <Competition item={opened.item} level={opened.level} ours={ours} />}

            {!opened && (
              <>
                {isOir && withEntries > 0 && (
                  <p className="cc-hint">
                    Tap a competition to see its running order once it's out. Competitor numbers come out at 7:00 am on each
                    competition's day.
                  </p>
                )}
                {!isOir && !namesShown && withEntries > 0 && (
                  <p className="cc-hint">
                    {isTeacher
                      ? "Tap a competition under one of your dancers to see your school's dancers in it. Everyone's names show once the organiser finishes merges and splits."
                      : "You can tap a competition to see who's in it once the organiser finishes merges and splits."}
                  </p>
                )}

                {hasMine && (
                  <div className="cc-vtabs" role="group" aria-label="Show">
                    <button type="button" aria-pressed={view === "mine"} className={`cc-vtab${view === "mine" ? " on" : ""}`} onClick={() => setTab("mine")}>
                      {isTeacher ? "Your students" : "Your competitions"} <span className="cc-vcount">{mineCount}</span>
                    </button>
                    <button type="button" aria-pressed={view === "all"} className={`cc-vtab${view === "all" ? " on" : ""}`} onClick={() => setTab("all")}>
                      All competitions
                    </button>
                  </div>
                )}

                {isTeacher && view === "mine" && (
                  <section className="cc-sec cc-yours">
                    <ul className="cc-kids">
                      {kids.map((d) => (
                        <li key={d.id} className="cc-kid">
                          <div className="cc-kid-head">
                            {d.number && <span className="cc-num">{d.number}</span>}
                            <span className="cc-kid-name">{d.name}</span>
                            <span className="cc-kid-count">{plural(d.comps.length, "dance", "dances")}</span>
                          </div>
                          <ul className="cc-list">
                            {d.comps.map((item) => (
                              <Row key={item.id} item={item} level={item.level} namesShown={namesShown} onOpen={() => openComp(item.id)} />
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {isTeacher && school && kids.length === 0 && !searching && <p className="cc-hint">None of your school's dancers are entered yet.</p>}

                {!isTeacher && view === "mine" && (
                  <section className="cc-sec cc-yours">
                    <ul className="cc-list">
                      {yours.map((item) => (
                        <Row
                          key={item.id}
                          item={item}
                          level={item.level}
                          yours={manyKids ? item.yours : null}
                          namesShown={namesShown}
                          onOpen={() => openComp(item.id)}
                        />
                      ))}
                    </ul>
                  </section>
                )}

                {view === "all" && (
                  <>
                    <div className="cc-bar">
                      <LevelTabs
                        sections={shown}
                        activeKey={searching ? null : current?.key}
                        onPick={(key) => {
                          setQuery("");
                          setLevel(key);
                        }}
                      />
                      <label className="cc-search">
                        <Icon name="search" />
                        <input
                          type="search"
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          placeholder="Find a competition or code"
                          aria-label="Find a competition"
                        />
                      </label>
                    </div>

                    {visible.map((sec) => (
                      <section className="cc-sec" key={sec.key}>
                        {/* the tab already names the level; a search lists each level it finds */}
                        {searching && (
                          <div className="cc-sec-head">
                            <span role="heading" aria-level={2}>
                              {sec.title}
                            </span>
                          </div>
                        )}
                        <ul className="cc-list">
                          {sec.items.map((item) => (
                            <React.Fragment key={item.id}>
                              <Row item={item} namesShown={namesShown} onOpen={() => openComp(item.id)} />
                              {item.subs.map((s) => (
                                <Row key={s.id} item={s} sub namesShown={namesShown} onOpen={() => openComp(s.id)} />
                              ))}
                            </React.Fragment>
                          ))}
                        </ul>
                      </section>
                    ))}
                    {shown.length === 0 && <p className="cc-empty">{searching ? "No competitions match your search." : "No competitions yet."}</p>}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CompetitionCounts;
